/* STKT V0.10: consent-gated acquisition details for Shopify contact emails.
 * No Google tag, third-party request, contact data or persistent visitor ID.
 */
(() => {
  'use strict';
  const script = document.querySelector('script[data-mw-stkt-attribution]');
  if (!script || script.dataset.enabled !== 'true' || window.Shopify?.designMode) return;

  const storageKey = 'mw-stkt-attribution-v1';
  const lifetime = 30 * 60 * 1000;
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign'];
  const clickKeys = ['gclid', 'gbraid', 'wbraid'];
  const allKeys = [...campaignKeys, ...clickKeys];
  const kinds = ['campaign', 'google', 'bing', 'social', 'other', 'unknown'];
  let current = null;
  let locationEvaluated = false;

  const allowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.marketingAllowed() === true &&
        privacy?.currentVisitorConsent().marketing === 'yes';
    } catch (_) { return false; }
  };
  const purge = () => {
    current = null;
    try { window.sessionStorage.removeItem(storageKey); } catch (_) { /* Storage can be blocked. */ }
  };
  const validValue = (key, value) => {
    if (typeof value !== 'string' || /^(undefined|null|true|false)$/i.test(value)) return false;
    return (clickKeys.includes(key) ? /^[A-Za-z0-9_-]{1,512}$/ : /^[A-Za-z0-9_-]{1,80}$/).test(value);
  };
  const validRecord = (value) => {
    if (!value || value.version !== 1 || !kinds.includes(value.kind) ||
        !Number.isFinite(value.at) || Date.now() - value.at < 0 || Date.now() - value.at >= lifetime ||
        !value.params || typeof value.params !== 'object' || Array.isArray(value.params)) return null;
    const params = {};
    for (const key of Object.keys(value.params)) {
      if (!allKeys.includes(key) || !validValue(key, value.params[key])) return null;
      params[key] = value.params[key];
    }
    if ((value.kind === 'campaign') !== (Object.keys(params).length > 0)) return null;
    return { version: 1, at: value.at, kind: value.kind, params };
  };
  const readRecord = () => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const record = validRecord(JSON.parse(raw));
      if (!record) window.sessionStorage.removeItem(storageKey);
      return record;
    } catch (_) { return null; }
  };
  const save = (record) => {
    current = record;
    try { window.sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch (_) { /* Keep this page's memory only. */ }
  };
  const normalizedHost = (host) => host.toLowerCase().replace(/^www\./, '');
  const hostMatches = (host, domain) => host === domain || host.endsWith('.' + domain);
  const classifyReferrer = (host) => {
    // Exact known search hosts: google.evil.example must never become Google.
    if (/^(?:[a-z0-9-]+\.)?google\.(?:ch|com|de|at|fr|it|li|co\.uk)$/.test(host)) return 'google';
    if (hostMatches(host, 'bing.com')) return 'bing';
    if (['facebook.com', 'instagram.com', 'linkedin.com', 't.co', 'x.com', 'tiktok.com', 'youtube.com'].some(domain => hostMatches(host, domain))) return 'social';
    return 'other';
  };
  const collect = () => {
    // Called only after the explicit consent gate. Never retain raw URLs/referrers.
    let url;
    try { url = new URL(window.location.href); } catch (_) { return; }
    let referrerHost = '';
    try { if (document.referrer) referrerHost = normalizedHost(new URL(document.referrer).hostname); } catch (_) { /* Unknown source. */ }
    const localHosts = [normalizedHost(url.hostname), 'stkt.ch'];
    if (typeof window.Shopify?.shop === 'string') localHosts.push(normalizedHost(window.Shopify.shop));
    const internal = !!referrerHost && localHosts.includes(referrerHost);
    let returning = false;
    try {
      returning = ['reload', 'back_forward'].includes(window.performance?.getEntriesByType('navigation')?.[0]?.type);
    } catch (_) { /* The Navigation API is optional. */ }
    const formResponse = url.searchParams.has('contact_posted') ||
      !!document.querySelector('[data-mw-stkt-form-success]');
    if (internal || returning || formResponse) {
      if (!current) save({ version: 1, at: Date.now(), kind: 'unknown', params: {} });
      return;
    }
    const params = {};
    for (const key of allKeys) {
      const values = url.searchParams.getAll(key);
      if (values.length === 1 && validValue(key, values[0])) params[key] = values[0];
    }
    const kind = Object.keys(params).length ? 'campaign' : referrerHost ? classifyReferrer(referrerHost) : 'unknown';
    // A genuinely new campaign/external arrival can replace the last observed
    // external source. Direct visits and internal navigation cannot overwrite it.
    if (kind !== 'unknown' || !current) save({ version: 1, at: Date.now(), kind, params });
  };
  const sourceLabel = (record) => {
    if (!record) return 'Nicht erfasst';
    if (record.kind === 'campaign') {
      if (clickKeys.some(key => record.params[key])) return 'Google Ads (Kennung vorhanden)';
      if (record.params.utm_source?.toLowerCase() === 'google' &&
          ['cpc', 'ppc', 'paidsearch'].includes(record.params.utm_medium?.toLowerCase())) return 'Google Ads (UTM, nicht bestätigt)';
      return 'Kampagnenlink (UTM)';
    }
    return { google: 'Google (Kanal unklar)', bing: 'Bing (Kanal unklar)',
      social: 'Social Media (Verweis)', other: 'Andere Website (Verweis)', unknown: 'Direkt / unbekannt' }[record.kind];
  };
  const evidenceLabel = (record) => {
    if (!record) return 'Ohne Herkunftserfassung';
    const evidence = clickKeys.filter(key => record.params[key]).map(key => key.toUpperCase());
    if (campaignKeys.some(key => record.params[key])) evidence.push('UTM');
    return evidence.join(' + ') || (record.kind === 'unknown' ? 'Keine Kennung' : 'Verweis');
  };
  const updateFields = () => {
    for (const field of document.querySelectorAll('[data-mw-stkt-attribution-field]')) {
      const key = field.dataset.mwStktAttributionField;
      if (key === 'source') field.value = sourceLabel(current);
      else if (key === 'evidence') field.value = evidenceLabel(current);
      else if (allKeys.includes(key)) {
        field.value = current?.params[key] || '';
        field.disabled = !field.value;
      }
    }
  };
  const refresh = () => {
    if (!allowed()) {
      purge();
      locationEvaluated = false;
      updateFields();
      return;
    }
    current = validRecord(current) || readRecord();
    if (!locationEvaluated) {
      collect();
      locationEvaluated = true;
    }
    updateFields();
  };
  document.addEventListener('visitorConsentCollected', refresh);
  document.addEventListener('shopify:section:load', refresh);
  // Capture phase updates fields before the existing functional submit handler.
  // No preventDefault: analytics failures must never block an enquiry.
  document.addEventListener('submit', (event) => {
    if (event.target?.querySelector?.('[data-mw-stkt-attribution-field]')) refresh();
  }, true);
  window.addEventListener('pageshow', refresh);
  if (window.Shopify?.customerPrivacy) refresh();
  else if (typeof window.Shopify?.loadFeatures === 'function') {
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        if (!error) refresh();
        else { purge(); updateFields(); }
      });
    } catch (_) { purge(); updateFields(); }
  }
})();
